import React, { useState } from "react";
import { toast } from "react-toastify";
import ClinicGroupAPI, { ClinicGroup } from "../../services/clinicGroup.service";

interface Props {
  group: ClinicGroup | null;
  onChange: (group: ClinicGroup) => void;
}

/** Minimal group-management affordance: create a group, or attach the
 * caller's own clinic to a group they already own. Full cross-clinic
 * invite/consent flows are out of scope for v1 (see group_service.py). */
export const ClinicGroupPanel: React.FC<Props> = ({ group, onChange }) => {
  const [expanded, setExpanded] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const created = await ClinicGroupAPI.create(name.trim());
      onChange(created);
      setName("");
      toast.success("Clinic group created");
    } catch (err: any) {
      toast.error(err?.message || "Failed to create clinic group");
    } finally {
      setBusy(false);
    }
  };

  const handleAttach = async () => {
    if (!group) return;
    setBusy(true);
    try {
      const updated = await ClinicGroupAPI.attachOwnClinic(group.clinic_group_id);
      onChange(updated);
      toast.success("This clinic is now part of the group");
    } catch (err: any) {
      toast.error(err?.message || "Failed to attach clinic");
    } finally {
      setBusy(false);
    }
  };

  if (!expanded && !group) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="text-xs font-medium text-blue-600 hover:underline px-1"
      >
        Run multiple branches? Set up a clinic group for consolidated reports →
      </button>
    );
  }

  if (group) {
    return (
      <div className="rounded-xl bg-blue-50 border border-blue-100 px-4 py-3 text-sm flex flex-wrap items-center justify-between gap-2">
        <div>
          <span className="font-medium text-slate-800">{group.name}</span>
          <span className="text-slate-500 ml-2">
            {group.member_clinics.length} branch{group.member_clinics.length === 1 ? "" : "es"}:{" "}
            {group.member_clinics.map((c) => c.name).join(", ") || "none yet"}
          </span>
        </div>
        <button
          onClick={handleAttach}
          disabled={busy}
          className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {busy ? "…" : "Add this clinic to the group"}
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-blue-50 border border-blue-100 px-4 py-3 text-sm flex flex-wrap items-center gap-2">
      <span className="text-slate-600">New clinic group name:</span>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. Sharma Clinics Group"
        className="border border-blue-200 rounded-lg px-3 py-1.5 text-sm flex-1 min-w-[180px]"
      />
      <button
        onClick={handleCreate}
        disabled={busy || !name.trim()}
        className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
      >
        {busy ? "…" : "Create"}
      </button>
      <button
        onClick={() => setExpanded(false)}
        className="text-xs text-slate-400 hover:text-slate-600"
      >
        Cancel
      </button>
    </div>
  );
};

export default ClinicGroupPanel;
